import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { DELIVERABLES } from "@/lib/content";
import { SectionHeading, MethodTag } from "./Primitives";
import { ApartmentMap } from "./ApartmentMap";

const Deliverable = ({ d, open, onToggle, onHover }) => (
  <li className="border-t border-divider first:border-t-0 sm:py-7 sm:first:pt-0" data-testid={`deliverable-${d.n}`} onMouseEnter={() => d.location && onHover(d.location)}>
    <div className="grid grid-cols-[2.25rem_1fr] sm:grid-cols-[3.5rem_1fr] gap-x-3 sm:gap-x-5">
      <span className="mono text-sec pt-5 sm:pt-1.5">{d.n}</span>
      <div className="min-w-0">
        <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`deliverable-detail-${d.n}`} data-testid={`deliverable-toggle-${d.n}`} className="sm:hidden flex w-full items-start justify-between gap-3 py-4 text-left min-h-[56px]">
          <span>
            <span className="block text-lg font-semibold tracking-tight text-ink leading-snug">{d.title}</span>
            <span className={`mt-1.5 block text-[15px] leading-relaxed text-sec ${open ? "" : "line-clamp-2"}`}>{d.plain}</span>
          </span>
          <ChevronDown size={18} className={`mt-1.5 shrink-0 text-forest transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="hidden sm:block">
          <h3 className="text-xl font-semibold tracking-tight text-ink">{d.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <MethodTag kind={d.kind} />
            <span className="mono text-sec">{d.where} · {d.unit}</span>
          </div>
          <p className="mt-3 text-base leading-relaxed text-ink">{d.plain}</p>
          <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`deliverable-detail-${d.n}`} data-testid={`deliverable-toggle-desktop-${d.n}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:underline underline-offset-4 min-h-[44px]">
            Technical details <ChevronDown size={16} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
        {open && (
          <div id={`deliverable-detail-${d.n}`} className="mb-5 sm:mb-0 sm:mt-1 reveal" data-testid={`deliverable-detail-${d.n}`}>
            <div className="sm:hidden mb-3 flex flex-wrap items-center gap-2">
              <MethodTag kind={d.kind} />
              <span className="mono text-sec">{d.where} · {d.unit}</span>
            </div>
            <p className="rounded-md bg-mint px-4 py-4 text-sm leading-relaxed text-ink">{d.technical}</p>
          </div>
        )}
      </div>
    </div>
  </li>
);

export const Assessment = () => {
  const [open, setOpen] = useState(null);
  const [spot, setSpot] = useState("bedroom");
  return (
    <section className="bg-white py-16 sm:py-28 scroll-mt-16 lg:scroll-mt-20 rule" id="what-we-test" data-testid="assessment-section">
      <div className="container-x">
        <SectionHeading
          eyebrow="What we test"
          title="Here’s what we do in your home."
          lead="Water testing, seven-day air monitoring, and gas-cooking NO₂ screening, with exactly what each includes. Laboratory analysis and on-site monitoring are labeled separately, with the sampling location and units for each."
          testId="assessment-heading"
        />
        <div className="mt-10 sm:mt-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <ol className="lg:col-span-7 list-none m-0 p-0 border-b border-divider sm:border-b-0" data-testid="deliverables-list">
            {DELIVERABLES.map((d) => <Deliverable key={d.n} d={d} open={open === d.n} onToggle={() => setOpen(open === d.n ? null : d.n)} onHover={setSpot} />)}
          </ol>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <ApartmentMap selected={spot} onSelect={setSpot} />
              <p className="mt-4 text-xs text-sec leading-relaxed">Simplified layout for illustration. Every basic inclusion above applies regardless of your home’s floor plan.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
